/**
 * `CancelSubscriptionDialog` — user-facing cancel-at-period-end confirm.
 *
 * Wraps the shared `PromptDialog` primitive with the copy the billing hero
 * needs: an honest preview of when access ends, a soft ask for a reason (not
 * required), and a danger-styled confirm. The parent owns the mutation +
 * loading state; this component is presentational.
 *
 * Reason capture is required (per ADR-0047): the churn signal is worth more
 * than the tiny fraction of users a required field turns away, and the audit
 * entry is more useful when it always carries a "why". The Zod boundary caps
 * the value at 500 chars — same cap that `PromptDialog`'s textarea enforces
 * client-side.
 *
 * @see docs/adr/0047-cancel-at-period-end.md
 */
import { PromptDialog } from "@flint/ui";

export interface CancelSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The date shown in the "keeps working until" copy — Razorpay's `current_end`. */
  currentPeriodEndsOn: string | null;
  /** Called with the (non-empty, trimmed) reason on confirm; may return a promise. */
  onConfirm: (reason: string) => void | Promise<void>;
  /** Bubble the mutation's pending state through to the confirm button. */
  pending: boolean;
}

function formatDate(iso: string): string {
  return new Date(iso + "T00:00:00Z").toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function CancelSubscriptionDialog({
  open,
  onOpenChange,
  currentPeriodEndsOn,
  onConfirm,
  pending,
}: CancelSubscriptionDialogProps) {
  const endDate = currentPeriodEndsOn ? formatDate(currentPeriodEndsOn) : null;
  const description = endDate ? (
    <>
      Your workspace will keep working until <strong>{endDate}</strong>. After
      that, you can't access it until you reactivate. You can undo this any
      time before the end of the period by talking to us.
    </>
  ) : (
    // Trial-only rows (no Razorpay mandate yet): we can't show an end date
    // because Razorpay's `current_end` is only populated after activation.
    // Fall back to the trial-end language handled server-side.
    <>
      Cancelling now stops any future billing. Your workspace stays usable
      until your trial ends; after that, you'll need to subscribe to keep
      using it.
    </>
  );

  return (
    <PromptDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Cancel your subscription?"
      description={description}
      label="Why are you cancelling?"
      placeholder="Tell us what went wrong — this helps us improve"
      intent="danger"
      multiline
      confirmLabel="Yes, cancel"
      cancelLabel="Keep plan"
      pending={pending}
      onConfirm={(value) => onConfirm(value.trim())}
    />
  );
}
